import {
  useCallback,
  useMemo,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import type { AttentionItem, SlackThread, CurrentUserIdentity } from '../../domain/types';
import { mapSlackThreadToAttentionItem } from '../../domain/mappers';
import { slackClient } from '../../services/clients';
import { toErrorMessage, slackSendErrorMessage } from '../../services/errors';
import {
  applyHydratedSlackItem,
  findSlackHydrateTarget,
  shouldSkipStubHydrate,
  upsertRawSlackThread,
} from './slackHydrateHelpers';

export interface SlackSliceOptions {
  currentUser?: CurrentUserIdentity;
  itemsRef: MutableRefObject<AttentionItem[]>;
  setItems: Dispatch<SetStateAction<AttentionItem[]>>;
  setErrors: Dispatch<SetStateAction<Record<string, string | null>>>;
  beginWrite: () => void;
  retry: (source?: string) => Promise<void>;
}

export function useSlackSlice({
  currentUser,
  itemsRef,
  setItems,
  setErrors,
  beginWrite,
  retry,
}: SlackSliceOptions) {
  const [rawSlackThreads, setRawSlackThreads] = useState<SlackThread[]>([]);
  const [threadSummaryOpen, setThreadSummaryOpen] = useState<Record<string, boolean>>({});
  const [mergeSet, setMergeSet] = useState<string[]>([]);

  const slackHydrateInFlightRef = useRef<Set<string>>(new Set());
  const hydrateSlackThreadRef = useRef<(itemId: string) => Promise<void>>(async () => undefined);

  const toggleThreadSummary = useCallback((itemId: string) => {
    setThreadSummaryOpen((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  }, []);

  const isThreadSummaryOpen = useCallback(
    (itemId: string) => {
      return !!threadSummaryOpen[itemId];
    },
    [threadSummaryOpen],
  );

  const resolveSlackThreadKey = useCallback(
    (itemId: string): { channelName: string; threadKey: string } => {
      const item = itemsRef.current.find((entry) => entry.id === itemId);
      const thread = item?.rawSlackThread;
      return {
        channelName: item?.primary || thread?.channel.name || itemId,
        threadKey: thread?.attentionId || thread?.threadTs || itemId,
      };
    },
    [itemsRef],
  );

  const sendThreadReply = useCallback(
    async (itemId: string, text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed) return false;

      beginWrite();
      setErrors((prev) => ({ ...prev, slack: null }));

      const now = new Date();
      const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const optimisticTs = `${Math.floor(now.getTime() / 1000)}.000${Date.now() % 1000}`;
      const userHandle =
        currentUser?.providerHandle || currentUser?.email?.split('@')[0] || 'currentUser';
      const userDisplayName = currentUser?.displayName ? `${currentUser.displayName} (tú)` : 'Tú';
      const userInitials = currentUser?.displayName
        ? currentUser.displayName
            .split(' ')
            .map((n) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()
        : 'TU';
      const newMsg = {
        ts: optimisticTs,
        user: {
          id: currentUser?.uid || 'U_CURRENT',
          name: userHandle,
          realName: userDisplayName,
          initials: userInitials,
          color: 'var(--z-accent)',
        },
        text: trimmed,
        timeFormatted: timeStr,
        isoTime: now.toISOString(),
        reactions: [],
      };

      setItems((prev) => {
        if (!prev.some((item) => item.id === itemId)) {
          return prev;
        }
        return prev.map((item) => {
          if (item.id !== itemId) return item;
          const existingMessages = item.messages || [];
          return {
            ...item,
            messages: [...existingMessages, newMsg],
          };
        });
      });

      try {
        const { channelName, threadKey } = resolveSlackThreadKey(itemId);
        const res = await slackClient.postReply(channelName, threadKey, trimmed);
        const mapped = mapSlackThreadToAttentionItem(
          res.data,
          res.data.zuneloMeta?.status,
          itemsRef.current.find((entry) => entry.id === itemId)?.ai,
          itemsRef.current.find((entry) => entry.id === itemId)?.next,
        );
        setRawSlackThreads((prev) => {
          const without = prev.filter(
            (thread) =>
              thread.attentionId !== res.data.attentionId && thread.threadTs !== res.data.threadTs,
          );
          return [...without, res.data];
        });
        setItems((prev) =>
          prev.map((item) => {
            if (item.id !== itemId) return item;
            return {
              ...mapped,
              id: itemId,
              messages: mapped.messages?.length
                ? mapped.messages
                : [...(item.messages || []), newMsg],
            };
          }),
        );
        return true;
      } catch (err) {
        setItems((prev) =>
          prev.map((item) => {
            if (item.id !== itemId) return item;
            return {
              ...item,
              messages: (item.messages || []).filter((message) => message.ts !== optimisticTs),
            };
          }),
        );
        setErrors((prev) => ({
          ...prev,
          slack: slackSendErrorMessage(err),
        }));
        return false;
      }
    },
    [beginWrite, setErrors, currentUser, setItems, resolveSlackThreadKey, itemsRef],
  );

  const generateThreadSummary = useCallback(
    async (itemId: string) => {
      setThreadSummaryOpen((prev) => ({ ...prev, [itemId]: true }));

      try {
        const { channelName, threadKey } = resolveSlackThreadKey(itemId);
        const res = await slackClient.generateAiSummary(channelName, threadKey, {
          force: true,
          mode: 'full',
        });
        setItems((prev) =>
          prev.map((entry) =>
            entry.id === itemId
              ? { ...entry, ai: res.data.summary, next: res.data.actionItem }
              : entry,
          ),
        );
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          slack: toErrorMessage(err, 'No se pudo generar el resumen del hilo.'),
        }));
        await retry('slack');
      }
    },
    [resolveSlackThreadKey, setItems, setErrors, retry],
  );

  const generateThreadDraft = useCallback(
    async (itemId: string, hint?: string): Promise<string | null> => {
      setErrors((prev) => ({ ...prev, slack: null }));
      try {
        const { channelName, threadKey } = resolveSlackThreadKey(itemId);
        const res = await slackClient.generateReplyDraft(channelName, threadKey, hint);
        const draft = res.data.draft?.trim();
        return draft || null;
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          slack: toErrorMessage(err, 'No se pudo generar el borrador con IA.'),
        }));
        return null;
      }
    },
    [setErrors, resolveSlackThreadKey],
  );

  const hydrateSlackThread = useCallback(
    async (itemId: string) => {
      const item = findSlackHydrateTarget(itemsRef.current, itemId);
      if (!item) return;
      if (slackHydrateInFlightRef.current.has(itemId)) return;
      slackHydrateInFlightRef.current.add(itemId);

      try {
        const { threadKey } = resolveSlackThreadKey(itemId);
        const res = await slackClient.getThread(threadKey);
        if (!res.data) return;
        if (shouldSkipStubHydrate(res.data.messagesHydrated, item.messages?.length ?? 0)) return;
        const mapped = mapSlackThreadToAttentionItem(
          res.data,
          res.data.zuneloMeta?.status,
          item.ai,
          item.next,
        );
        setRawSlackThreads((prev) => upsertRawSlackThread(prev, res.data!));
        setItems((prev) => applyHydratedSlackItem(prev, itemId, mapped, currentUser));
      } catch {
        // Keep the stub card visible
      } finally {
        slackHydrateInFlightRef.current.delete(itemId);
      }
    },
    [itemsRef, resolveSlackThreadKey, setItems, currentUser],
  );
  hydrateSlackThreadRef.current = hydrateSlackThread;

  const toggleMergeThread = useCallback((itemId: string) => {
    setMergeSet((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  }, []);

  const clearMergeSet = useCallback(() => {
    setMergeSet([]);
  }, []);

  return useMemo(
    () => ({
      rawSlackThreads,
      setRawSlackThreads,
      threadSummaryOpen,
      toggleThreadSummary,
      isThreadSummaryOpen,
      sendThreadReply,
      generateThreadSummary,
      generateThreadDraft,
      toggleMergeThread,
      mergeSet,
      clearMergeSet,
      hydrateSlackThread,
    }),
    [
      rawSlackThreads,
      threadSummaryOpen,
      toggleThreadSummary,
      isThreadSummaryOpen,
      sendThreadReply,
      generateThreadSummary,
      generateThreadDraft,
      toggleMergeThread,
      mergeSet,
      clearMergeSet,
      hydrateSlackThread,
    ],
  );
}
